/**
 * Iterate through all campaigns in clientCampaigns
 * @param {Object} clientCampaigns - The campaigns data structure
 * @param {Function} convertTimestampsToDates - Function to convert timestamps
 * @param {Function} callback - Callback function called for each campaign with (campaign, campaignId, clientId)
 */
export const iterateCampaigns = (clientCampaigns, convertTimestampsToDates, callback) => {
    if (!clientCampaigns) return;
    
    Object.entries(clientCampaigns).forEach(([clientId, clientArray]) => {
        clientArray[0]?.campaigns?.forEach(original_campaign => {
            const campaign = convertTimestampsToDates(original_campaign);
            const campaignId = campaign.campaign_id;
            callback(campaign, campaignId, clientId);
        });
    });
};

/**
 * Find the client ID for a given campaign ID
 * @param {Object} clientCampaigns - The campaigns data structure
 * @param {string} campaignId - The campaign ID to find
 * @returns {string|null} The client ID or null if not found
 */
export const findClientIdByCampaignId = (clientCampaigns, campaignId) => {
    if (!clientCampaigns) return null;
    
    for (const [clientId, clientArray] of Object.entries(clientCampaigns)) {
        const campaign = clientArray[0]?.campaigns?.find(c => c.campaign_id === campaignId);
        if (campaign) {
            return clientId;
        }
    }
    return null;
};

/**
 * Validate date range
 * @param {string} fromDate - Start date
 * @param {string} toDate - End date
 * @param {string} campaignId - Campaign ID for error message
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateDateRange = (fromDate, toDate, campaignId) => {
    if (!fromDate || !toDate) {
        return {
            valid: false,
            error: `Campaign ${campaignId} is missing from or to date. Please set both dates before proceeding.`
        };
    }
    
    if (new Date(fromDate) > new Date(toDate)) {
        return {
            valid: false,
            error: `Campaign ${campaignId} has invalid date range: from date (${fromDate}) cannot be after to date (${toDate}).`
        };
    }
    
    return { valid: true, error: null };
};

/**
 * Create batch job options for campaign processing
 * @param {Object} params - Parameters object
 * @param {string} params.clientId - Client ID
 * @param {string} params.campaignId - Campaign ID
 * @param {string} params.fromDate - Start date
 * @param {string} params.toDate - End date
 * @param {string} params.projectId - Project ID
 * @param {boolean} params.createInvoice - Whether to create invoice
 * @param {boolean} params.attachInvoice - Whether to attach invoice
 * @param {boolean} params.updateReportHistory - Whether to update report history
 * @returns {Object} Batch job options
 */
export const createBatchJobOptions = ({
    clientId,
    campaignId,
    fromDate,
    toDate,
    projectId,
    createInvoice = false,
    attachInvoice = false,
    updateReportHistory = false
}) => {
    return {
        engine: "awslambda",
        projectPathSelector: {
            project: projectId,
            selectionType: "single",
            selectionValue: clientId,
            maxClients: 0
        },
        create_report_data: true,
        create_client_screenshot: true,
        create_pdf_report: true,
        create_invoice: createInvoice,
        create_mail: true,
        attach_report: true,
        attach_invoice: attachInvoice,
        update_report_history: updateReportHistory,
        from_date: fromDate,
        to_date: toDate,
        campaign_id: campaignId
    };
};

/**
 * Initialize table values object with default structure
 * @param {Object} tableValues - Current table values
 * @param {string} prefix - Prefix for the keys (e.g., 'sendInvoices', 'sendReports')
 * @returns {Object} Initialized table values
 */
export const initializeTableValues = (tableValues, prefix) => {
    const newTableValues = { ...tableValues };
    if (!newTableValues[`${prefix}FromDate`]) {
        newTableValues[`${prefix}FromDate`] = {};
    }
    if (!newTableValues[`${prefix}ToDate`]) {
        newTableValues[`${prefix}ToDate`] = {};
    }
    if (!newTableValues[`${prefix}Action`]) {
        newTableValues[`${prefix}Action`] = {};
    }
    return newTableValues;
};

